import { Form as InertiaForm } from '@inertiajs/react'
import { type ComponentProps } from 'react'

import { AreaAgentOptionType, AreaType, CommunityOptionType } from './types.ts'

type FormProps = Omit<ComponentProps<typeof InertiaForm>, 'children'> & {
  area: AreaType
  agents?: AreaAgentOptionType[]
  communities?: CommunityOptionType[]
  can_select_community?: boolean
  submitText: string
}

export default function Form({ area, agents = [], communities = [], can_select_community = false, submitText, ...formProps }: FormProps) {
  return (
    <InertiaForm
      className="contents"
      {...formProps}
    >
      {({ errors, processing }) => (
        <>
          <div className="my-5">
            <label htmlFor="name">Nom</label>
            <input
              type="text"
              name="area[name]"
              id="name"
              defaultValue={area.name}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.name && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.name.join(', ')}
              </div>
            )}
          </div>

          {can_select_community && communities.length > 0 && (
            <div className="my-5">
              <label htmlFor="community_id">Commune</label>
              <select
                id="community_id"
                name="area[community_id]"
                defaultValue={area.community_id || communities[0]?.id || ""}
                className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
                required
              >
                <option value="" disabled>Sélectionner une commune</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name} ({community.code})
                  </option>
                ))}
              </select>
              {errors.community_id && (
                <div className="text-red-500 px-3 py-2 font-medium">
                  {errors.community_id.join(', ')}
                </div>
              )}
            </div>
          )}

          <div className="my-5">
            <label htmlFor="assigned_agent_id">Agent assigné</label>
            <select
              id="assigned_agent_id"
              name="area[user_id]"
              defaultValue={area.user_id || ""}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            >
              <option value="">Aucun agent assigné</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}{agent.code ? ` (${agent.code})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="my-5">
            <label htmlFor="address_display">Adresse</label>
            <input
              type="text"
              id="address_display"
              name="area[address]"
              defaultValue={area.address || ''}
              placeholder="Saisir l'adresse de la zone"
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            />
            {errors.address && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.address.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="area[description]"
              defaultValue={area.description || ''}
              rows={4}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
              placeholder="Description de la zone"
            />
            {errors.description && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.description.join(', ')}
              </div>
            )}
          </div>

          <div className="my-5">
            <label htmlFor="status">Statut</label>
            <select
              name="area[status]"
              id="status"
              defaultValue={area.status}
              className="block shadow rounded-md border border-gray-400 outline-none px-3 py-2 mt-2 w-full"
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
            {errors.status && (
              <div className="text-red-500 px-3 py-2 font-medium">
                {errors.status.join(', ')}
              </div>
            )}
          </div>

          <div className="inline">
            <button
              type="submit"
              disabled={processing}
              className="rounded-lg py-3 px-5 bg-blue-600 text-white inline-block font-medium cursor-pointer"
            >
              {submitText}
            </button>
          </div>
        </>
      )}
    </InertiaForm>
  )
}
